import { LOCATION_CHANGED, LocationChangedAction } from 'redux-little-router';

import { Action } from '../actions';
import { getColumnsSettings } from '../declarations/common';
import { RoutesPath } from '../routes/index';
import { LogInitialState } from '../service/LogHelpers/LogHelpers';
import { recommendationsInitialState } from '../service/RecommendationsHelpers/RecommendationsHelpers';
import { ReducerCustom } from '../service/Redux/Redux';
import { UrlHelpers } from '../service/UrlHelpers';
import { EditBannersState } from '../typings/bannersEditState';
import { EditCampaignsState } from '../typings/campaignsEdit';
import { ChatListEditorState } from '../typings/chatList';
import { defaultRowsCount } from '../typings/grid';
import { EditGroupsState } from '../typings/groupsEditState';
import { EditInternalBannersState } from '../typings/internalBannersEditState';
import { MarketerCardData, Marketers } from '../typings/marketers';
import { MetrikaCounterEditorState } from '../typings/metrikaCounterEditor';
import { Products } from '../typings/products';
import { RetargetingConditionEditorState } from '../typings/retargeting';
import {
    CommonState,
    CryptaAudioGenresEditorsState,
    CryptaBehavioralEditorsState,
    CryptaGroupFamilyEditorsState,
    CryptaSocialDemoEditorsState,
    GridState,
    HelpState,
    LogState,
    RecommendationsGridState,
    RecommendationsState,
    State
} from '../typings/state';
import { StrategyEditorState } from '../typings/strategyEditor';

import { getEditBannerPageData } from './bannersEdit';
import { getEditCampaignsPageData } from './campaignsEdit';
import { chatListInitialState } from './chatListEditor/chatListEditor';
import { resetSidebar } from './common/helpers';
import { resetGridStateReducer } from './grid/helpers';
import { getEditGroupPageData } from './groupsEdit';
import { helpInitialState } from './help';
import { getEditInternalBannerPageData } from './internalBannersEdit';
import { marketerCardInitialState } from './marketers/marketerCard';
import { marketersInitialState } from './marketers/marketersList';
import { metrikaCounterEditorInitialState } from './metrikaCounterEditor';
import { productsInitialState } from './products';
import { defaultRetargetingEditorState } from './retargetingConditionEditor';
import { strategyEditorInitialState } from './strategyEditor';
import { cryptaAudioGenresEditorsInitialState } from './targetings/cryptaAudioGenres';
import { cryptaBehavioralEditorsInitialState } from './targetings/cryptaBehavioral';
import { cryptaGroupFamilyEditorsInitialState } from './targetings/cryptaGroupFamily';
import { cryptaSocialDemoEditorsInitialState } from './targetings/cryptaSocialDemo';

const gridRouteFlow = (
    state: GridState,
    action: LocationChangedAction,
    fullState: State
): GridState => {
    const { route: newRoute } = action.payload;
    const previousRouter = fullState.router.previous;
    if (
        newRoute === RoutesPath.grid ||
        newRoute === RoutesPath.shortGrid ||
        newRoute === RoutesPath.bannersEdit ||
        newRoute === RoutesPath.campaignsEdit ||
        newRoute === RoutesPath.groupsEdit ||
        newRoute === RoutesPath.internalBannersEdit
    ) {
        if (previousRouter) {
            if (
                previousRouter.route === RoutesPath.bannersEdit ||
                previousRouter.route === RoutesPath.campaignsEdit ||
                previousRouter.route === RoutesPath.groupsEdit ||
                previousRouter.route === RoutesPath.grid ||
                previousRouter.route === RoutesPath.internalBannersEdit
            ) {
                return resetGridStateReducer(
                    state,
                    fullState,
                    state.selectionInfo
                );
            }
        }
    }

    return resetGridStateReducer(state, fullState);
};

const commonRouteFlow = (state: CommonState): CommonState => {
    return {
        ...resetSidebar(state),
        screenError: {
            isVisible: false
        }
    };
};

const recommendationsRouteFlow = (
    state: RecommendationsState,
    action: LocationChangedAction
): RecommendationsState => {
    if (action.payload.route !== RoutesPath.recommendationsPage) {
        return state;
    }

    return recommendationsInitialState;
};

const recommendationsGridRouteFlow = (
    state: RecommendationsGridState,
    action: LocationChangedAction
): RecommendationsGridState => {
    if (action.payload.route !== RoutesPath.recommendationsItems) {
        return state;
    }

    return {
        ...state,
        summaryRecommendationData: undefined,
        gridData: {
            ...state.gridData,
            allItemsIds: [],
            data: [],
            total: defaultRowsCount
        }
    };
};

const logRouteFlow = (
    state: LogState,
    action: LocationChangedAction
): LogState => {
    if (action.payload.route !== RoutesPath.log) {
        return state;
    }

    return {
        ...LogInitialState,
        columnsSettings: getColumnsSettings(),
        sourceParams: state.sourceParams
    };
};

const helpRouteFlow = (
    state: HelpState,
    action: LocationChangedAction
): HelpState => {
    return helpInitialState;
};

const editGroupsFlow = (
    state: EditGroupsState,
    action: LocationChangedAction,
    fullState: State
): EditGroupsState => {
    if (action.payload.route !== RoutesPath.groupsEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditGroupPageData(queryParams)
    };
};

const editCampaignsFlow = (
    state: EditCampaignsState,
    action: LocationChangedAction,
    fullState: State
): EditCampaignsState => {
    if (action.payload.route !== RoutesPath.campaignsEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditCampaignsPageData(queryParams)
    };
};

const editBannersFlow = (
    state: EditBannersState,
    action: LocationChangedAction,
    fullState: State
): EditBannersState => {
    if (action.payload.route !== RoutesPath.bannersEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditBannerPageData(queryParams)
    };
};

const editInternalBannersFlow = (
    state: EditInternalBannersState,
    action: LocationChangedAction,
    fullState: State
): EditInternalBannersState => {
    if (action.payload.route !== RoutesPath.internalBannersEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditInternalBannerPageData(queryParams)
    };
};

const retargetingConditionEditorFlow = (
    state: RetargetingConditionEditorState
): RetargetingConditionEditorState => {
    return defaultRetargetingEditorState;
};

const chatListEditorStateFlow = (
    state: ChatListEditorState
): ChatListEditorState => {
    return chatListInitialState;
};

const metrikaCounterEditorFlow = (
    state: MetrikaCounterEditorState
): MetrikaCounterEditorState => {
    return metrikaCounterEditorInitialState;
};

const strategyEditorFlow = (
    state: StrategyEditorState
): StrategyEditorState => {
    return strategyEditorInitialState;
};

const cryptaSocialDemoEditorStateFlow = (
    state: CryptaSocialDemoEditorsState
): CryptaSocialDemoEditorsState => {
    return cryptaSocialDemoEditorsInitialState;
};

const cryptaGroupFamilyEditorStateFlow = (
    state: CryptaGroupFamilyEditorsState
): CryptaGroupFamilyEditorsState => {
    return cryptaGroupFamilyEditorsInitialState;
};

const cryptaBehavioralEditorStateFlow = (
    state: CryptaBehavioralEditorsState
): CryptaBehavioralEditorsState => {
    return cryptaBehavioralEditorsInitialState;
};

const cryptaAudioGenresEditorStateFlow = (
    state: CryptaAudioGenresEditorsState
): CryptaBehavioralEditorsState => {
    return cryptaAudioGenresEditorsInitialState;
};

const internalProductsFlow = (
    state: Products,
    action: LocationChangedAction,
    fullState: State
): Products => {
    if (action.payload.route !== RoutesPath.products) {
        return state;
    }

    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return productsInitialState;
};

const marketersListFlow = (
    state: Marketers,
    action: LocationChangedAction,
    fullState: State
): Marketers => {
    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return marketersInitialState;
};

const marketerCardFlow = (
    state: MarketerCardData,
    action: LocationChangedAction,
    fullState: State
): MarketerCardData => {
    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return marketerCardInitialState;
};

const routeFlowWrapper = <K extends keyof State, T extends State[K]>(
    reducer: ReducerCustom<T>,
    routeFlowReducer: ReducerCustom<T>
): ReducerCustom<T> => {
    return (state: T, action: Action, fullState: State): T => {
        if (action.type === LOCATION_CHANGED) {
            return routeFlowReducer(state, action, fullState);
        }
        return reducer(state, action, fullState);
    };
};

export const withRouteFlow = <K extends keyof State>(
    reducers: Record<K, ReducerCustom<State[K]>>
): Record<K, ReducerCustom<State[K]>> => {
    return Object.keys(reducers).reduce(
        (acc, key: K) => {
            const reducer = reducers[key];
            let routeFlow: ReducerCustom<State[K]> | undefined = undefined;
            switch (key) {
                case 'recommendations':
                    routeFlow = recommendationsRouteFlow;
                    break;
                case 'recommendationsGrid':
                    routeFlow = recommendationsGridRouteFlow;
                    break;
                case 'commonData':
                    routeFlow = commonRouteFlow;
                    break;
                case 'grid':
                    routeFlow = gridRouteFlow;
                    break;
                case 'log':
                    routeFlow = logRouteFlow;
                    break;
                case 'help':
                    routeFlow = helpRouteFlow;
                    break;
                case 'editCampaigns':
                    routeFlow = editCampaignsFlow;
                    break;
                case 'editGroups':
                    routeFlow = editGroupsFlow;
                    break;
                case 'editBanners':
                    routeFlow = editBannersFlow;
                    break;
                case 'editInternalBanners':
                    routeFlow = editInternalBannersFlow;
                    break;
                case 'products':
                    routeFlow = internalProductsFlow;
                    break;
                case 'cryptaSocialDemoEditor':
                    routeFlow = cryptaSocialDemoEditorStateFlow;
                    break;
                case 'cryptaGroupFamilyEditor':
                    routeFlow = cryptaGroupFamilyEditorStateFlow;
                    break;
                case 'cryptaBehavioralEditor':
                    routeFlow = cryptaBehavioralEditorStateFlow;
                    break;
                case 'cryptaAudioGenresEditor':
                    routeFlow = cryptaAudioGenresEditorStateFlow;
                    break;
                case 'marketersList':
                    routeFlow = marketersListFlow;
                    break;
                case 'marketerCard':
                    routeFlow = marketerCardFlow;
                    break;
                case 'interestsTargetingEditor':
                case 'itemsListTextareaEditor':
                case 'notificationEditor':
                case 'rateCorrectionsEditor':
                case 'retargetingEditor':
                case 'regionsEditor':
                case 'datesIntervalEditor':
                case 'experimentsEditor':
                case 'editPageItemNameEditor':
                case 'metrikaKeyGoalsEditor':
                case 'minusPhraseLibraryEditor':
                case 'timeTargetingEditor':
                case 'relevantPhrasesEditor':
                case 'visitingCardEditor':
                    routeFlow = (): {} => ({});
                    break;
                case 'retargetingConditionEditor':
                    routeFlow = retargetingConditionEditorFlow;
                    break;
                case 'chatListEditor':
                    routeFlow = chatListEditorStateFlow;
                    break;
                case 'metrikaCounterEditor':
                    routeFlow = metrikaCounterEditorFlow;
                    break;
                case 'strategyEditor':
                    routeFlow = strategyEditorFlow;
                    break;
                default:
                    break;
            }
            return {
                ...acc,
                [key]: routeFlow
                    ? routeFlowWrapper(reducer, routeFlow)
                    : reducer
            };
        },
        {} as Record<K, ReducerCustom<State[K]>>
    );
};
